import { Period } from "@dto/period"
import Field, { AggregationType, FieldType } from "./fields"
import { Group, Pivot, Sorting, Summary } from "./views"
import { PATH_SEPARATOR, SOURCE_SEPARATOR } from "@utils/constants"
import { CompozzError } from "./CompozzError"

// Line represents the line of information return when requesting paths
type Line = {
	items: Item[]
	children: Line[] | null
	isLeftGrouped: boolean
	// Values of the requested summaries computed over the lines of this group,
	// index-aligned with the response columns (null entries for non-summarized
	// columns — pivoted columns sharing a path key are summarized each on its
	// own column). Only set on group header lines.
	summaries?: (any | null)[] | null
	_rowId?: string  // Stable row ID assigned at fetch time, does not change with frontend filtering
}

type Item = {
	key: ItemKey
	value: any
	readOnly: boolean
	aggregated: boolean
	parentKey: ParentKey | null
}

type FieldKey = {
	objectKey: string
	fieldKey: string
}

type ItemKey = FieldKey & {
	recordKey: string
}

type ParentKey = {
	objectKey: string
	recordKey: string
}

type ItemValue = {
	key: ItemKey
	value: any
}

type PathsRequest = {
	workspaceKey: string
	objectKey: string

	// List of paths to request
	paths: Path[]

	// Filter on record keys
	recordKeys: string[] | null

	// Filters to apply
	filter: Filter | null

	// Sortings to apply
	sortings: Sorting[] | null

	// Groups to apply
	groups: Group[] | null

	// Pivot to apply
	pivot: Pivot | null

	// Period to apply
	period: Period | null

	// Aggregations to apply
	aggregations: Map<string, AggregationType> | null

	// Summaries to compute, over the whole result set and per group
	summaries: Summary[] | null

	// When set, the response only carries the summaries and the group
	// skeleton (group lines without their records): used to refresh the
	// totals after a mutation without re-downloading the lines.
	summariesOnly?: boolean

	// Field order to apply
	fieldOrder: string[] | null

	// Limit to apply
	limit: number | null

	// Stateless page window over root records (invisible pagination). The
	// server honors it only for eligible flat views; the response echoes a
	// PageInfo when pagination was applied.
	page?: PageRequest | null
}

type PageRequest = {
	offset: number,
	size: number
}

type PageInfo = {
	offset: number,
	size: number,
	total: number,
	hasMore: boolean
}

type PathsResponse = {
	columns: FieldColumn[],
	lines: Line[],
	pivot: Field | null,
	// Values of the requested summaries computed over all the leaf lines of
	// the result set, index-aligned with columns (null entries for
	// non-summarized columns — pivoted columns sharing a path key are
	// summarized each on its own column). Group-level values are carried by
	// Line.summaries.
	summaries?: (any | null)[] | null,
	// Present ONLY when the server paginated the request.
	page?: PageInfo | null
}

type Direction = "INBOUND" | "OUTBOUND"
export const OUTBOUND: Direction = "OUTBOUND";
export const INBOUND: Direction = "INBOUND";

export function getOppositeDirection(direction: Direction): Direction {
	return direction === OUTBOUND ? INBOUND : OUTBOUND;
}

type Operator = "EQUAL" | "NOT_EQUAL" | "INF" | "SUP" | "INF_EQUAL" | "SUP_EQUAL" | "CONTAINS" | "NOT_CONTAINS" | "HAS" | "NOT_HAS" | "IN" | "IS_EMPTY" | "IS_NOT_EMPTY"
export const EQUAL: Operator = "EQUAL";
export const NOT_EQUAL: Operator = "NOT_EQUAL";
export const INF: Operator = "INF";
export const SUP: Operator = "SUP";
export const INF_EQUAL: Operator = "INF_EQUAL";
export const SUP_EQUAL: Operator = "SUP_EQUAL";
export const CONTAINS: Operator = "CONTAINS";
export const NOT_CONTAINS: Operator = "NOT_CONTAINS";
export const HAS: Operator = "HAS";
export const NOT_HAS: Operator = "NOT_HAS";
export const IN: Operator = "IN";
export const IS_EMPTY: Operator = "IS_EMPTY";
export const IS_NOT_EMPTY: Operator = "IS_NOT_EMPTY";

type Combinator = "AND" | "OR"
export const AND: Combinator = "AND";
export const OR: Combinator = "OR";

export const FILTERABLE_TYPES = [
	FieldType.Text,
	FieldType.TextArea,
	FieldType.Url,
	FieldType.Number,
	FieldType.Decimal,
	FieldType.Devise,
	FieldType.Percent,
	FieldType.Date,
	FieldType.Boolean,
	FieldType.Link,
	FieldType.Choice,
	FieldType.MultiChoice,
	FieldType.User,
	FieldType.Period
];

export const GROUPABLE_TYPES = [
	FieldType.Text,
	FieldType.TextArea,
	FieldType.Url,
	FieldType.Number,
	FieldType.Decimal,
	FieldType.Devise,
	FieldType.Percent,
	FieldType.Date,
	FieldType.Boolean,
	FieldType.Link,
	FieldType.Choice,
	FieldType.MultiChoice,
	FieldType.User,
	FieldType.Period
];

// Types that can be aggregated in the summary lines of a view (numeric types only)
export const SUMMABLE_TYPES = [
	FieldType.Number,
	FieldType.Decimal,
	FieldType.Devise,
	FieldType.Percent
];

// Edge represent the atomic item of a relation
// This is composed of relation key and a direction (INBOUND or OUTBOUND)
// If not precised, direction is OUTBOUND
type Edge = {
	key: string
	direction: Direction
}

// Path represents a path into the model graph
// It is composed of a list of edges to reach a target object and field
type Path = {
	// Edges composing the path
	// Edges shall be consecutive.
	edges: Edge[]

	// Object Key
	object: string

	// Field Key
	field: string

	// Source Field Key
	// Filled only if the path is a relation
	srcField: string | null

	// Source Object Key
	// Filled only if the path is a relation
	srcObject: string | null

	// Aggregation Type
	aggregation: AggregationType
}

// SingleFilter is a single filter (leaf)
type SingleFilter = {
	pathKey: string
	operator: Operator
	value: any | null
	valueKey: string | null
	formula: string | null
}

// GroupFilter is a group of filters (node)
type GroupFilter = {
	combinator: Combinator
	filters: Filter[]
}

// Filter is a filter that can be either a group of filters or a single filter
// Both fields are optional, but at least one must be present. If both are present, the filter will be used.
type Filter = {
	groupFilter: GroupFilter | null
	singleFilter: SingleFilter | null
}

type UpdateRecordRequest = {
	workspaceKey: string
	ItemValues: ItemValue[]
	Replace: boolean
	// Undo scope (sent in the body to avoid CORS preflight on custom headers)
	sessionId?: string
	objectKey?: string
}

type Record = {
	_key: string,
	order: number,
	fields: Map<string, any>
}

type RecordsRequest = {
	workspaceKey: string
	objectKey: string

	// List of record keys to retrieve
	// If empty, then retrieve all the records
	recordKeys: string[]

	// List of fields to retrieve
	// Depending on the attribute FieldByName, the list will correspond either to field keys or field names
	fields: string[]

	// If true, then the fields in the Fields attribute shall correspond to field names
	// Otherwise, it corresponds to field keys
	fieldByName: boolean

	// Field filters
	filter: Filter | null
}

type NewRecordRequest = {
	workspaceKey: string
	objectKey: string

	// Map of values
	// Depending on the attribute FieldByName, map keys will be either field keys or field name
	fieldValues: Map<string, any>

	// If true, then the keys of FieldValues attribute shall correspond to field names
	// Otherwise, it corresponds to field keys
	fieldByName: boolean

	// If true, then links between records are performed using field values given in FieldValues attribute
	// Otherwise, it corresponds to record keys given in FieldValues attribute
	linkByValue: boolean

	// If true, then links between records are performed using field values given in FieldValues attribute for the given relation key
	// Otherwise, the behavior defined by the attribute linkByValue is used
	overridenLinkByValue?: Map<string, boolean>

	// Undo scope (sent in the body to avoid CORS preflight)
	sessionId?: string
}

type DeleteRecordRequest = {
	workspaceKey: string
	objectKey: string
	recordKeys: string[]
	// Undo scope (sent in the body to avoid CORS preflight on custom headers)
	sessionId?: string
}

type DuplicateRecordRequest = {
	workspaceKey: string
	objectKey: string
	recordKeys: string[]

	// Overriden information
	overridenFields: Map<string, Map<string, any>>
	overridenLinks: Map<string, Map<string, string[]>>

	// Undo scope (sent in the body to avoid CORS preflight)
	sessionId?: string
}

type RecordLinksRequest = {
	workspaceKey: string
	originObjKey: string
	relationKey: string
	srcRecordKey: string | null
	destRecordKeys: string[] | null
	direction: Direction | null
	linkKeys: string[] | null
	// Undo scope (sent in the body to avoid CORS preflight)
	sessionId?: string
}

type Relation = {
	_key: string
	_from: string
	_to: string
}

type MetaLine = {
	id: string,
	line: Line,
	parentId: string | null,
	childrenIds: string[],
	level: number
}

export function areLinesEqual(line1: Line, line2: Line, compareChildren: boolean = true): boolean {
	return line1.isLeftGrouped === line2.isLeftGrouped &&
		line1.items.length === line2.items.length &&
		line1.items.every((item1, index) => areItemsEqual(item1, line2.items[index])) &&
		// Group lines display their summaries: a change must invalidate the row memo
		JSON.stringify(line1.summaries ?? null) === JSON.stringify(line2.summaries ?? null) &&
		(compareChildren ? (line1.children?.length === line2.children?.length &&
			(!line1.children || line1.children.length === 0 ||
				line1.children!.every((child1, index) => areLinesEqual(child1, line2.children![index])))) : true);
}

export function areItemsEqual(item1: Item | null, item2: Item | null): boolean {
	
	if (item1?.value === null && item2?.value === null) {
		return true;
	}
	if (item1?.value === null || item2?.value === null) {
		return false;
	}
	return JSON.stringify(item1?.value) === JSON.stringify(item2?.value) && 
	item1?.key?.recordKey === item2?.key?.recordKey && 
	item1?.key?.objectKey === item2?.key?.objectKey && 
	item1?.key?.fieldKey === item2?.key?.fieldKey &&
	item1?.readOnly === item2?.readOnly;
}

export type FieldColumn = {
	pathKey: string | null,
	field: Field,
	srcField: Field | null,
	isPivot: boolean,
	pivotValue: Item | null,
	dependencies: string[] | null,
	// Set by the server when the column could not be served (deleted field or
	// relation on its path, formula depending on one): the cells are empty and
	// the header shows the warning icon.
	error?: CompozzError | null,
}

export type PatchItemRequest = {
	objectKey: string
	recordKey: string
	valuesByFieldKeys: Map<string, any>
}

// Compact form of a CSV import: the column names once, then one value array
// per record aligned on the columns (the backend also accepts a legacy `lines`
// form, one object per record, used by external callers).
export type ImportCsvRequest = {
	workspaceKey: string
	objectKey: string
	columns: string[]
	rows: any[][]
}

export function getSrcField(fieldColumn: FieldColumn): Field {
	return fieldColumn.srcField || fieldColumn.field;
}

// A column "is a link" when the field it actually edits is a Link field. For a
// relation-path column the editable field is `srcField` (the LINK field) while
// `field` is the destination scalar; getSrcField() resolves that. Single source
// of truth for link routing, avoids the `fieldColumn.field.type` foot-gun.
export function isLinkColumn(fieldColumn: FieldColumn): boolean {
	return getSrcField(fieldColumn).type === FieldType.Link;
}

export function extractFirstLinkPath(pathKey: string): string {
	const realPath = pathKey.substring(pathKey.indexOf(SOURCE_SEPARATOR) + 1);
	return realPath.substring(0, realPath.indexOf(PATH_SEPARATOR));
}

export function isRootPath(pathKey: string): boolean {
	const realPath = pathKey.substring(pathKey.indexOf(SOURCE_SEPARATOR) + 1);
	return realPath.split(PATH_SEPARATOR).length === 1;
}

export type { Line, Item, ItemKey, ParentKey, ItemValue, PathsRequest, Path, Edge, Filter, UpdateRecordRequest, Record, RecordLinksRequest, Direction, Relation, FieldKey, Operator, RecordsRequest, Combinator, SingleFilter, GroupFilter, NewRecordRequest, MetaLine, DeleteRecordRequest, DuplicateRecordRequest, PathsResponse, PageRequest, PageInfo };
